import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot, doc, deleteDoc, getDocs, orderBy, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { deleteUser } from 'firebase/auth';
import { auth, db, functions } from '../firebase';

const Dashboard = ({ isAdmin }) => {
  const [user] = useAuthState(auth);
  const [hasApplication, setHasApplication] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [phase2Codes, setPhase2Codes] = useState({ session1: '', session2: '' });
  const [codeSubmitting, setCodeSubmitting] = useState({ session1: false, session2: false });
  const [codeResults, setCodeResults] = useState({ session1: null, session2: null });
  const [applicationWindow, setApplicationWindow] = useState(null);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (!user || !user.uid) {
      setLoading(false);
      return;
    }

    const checkUserStatus = async () => {
      try {
        // Ensure user is fully authenticated
        await user.getIdToken();
        
        // Small delay to ensure authentication is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        // If admin, redirect to admin dashboard
        if (isAdmin) {
          window.location.href = '/admin';
          return;
        }

        // Check if user has already submitted an application
        const applicationsQuery = query(
          collection(db, 'applications'),
          where('userId', '==', user.uid)
        );

        const unsubscribe = onSnapshot(applicationsQuery, (snapshot) => {
          if (!snapshot.empty) {
            const application = snapshot.docs[0].data();
            setHasApplication(true);
            setApplicationStatus(application.status);
            setApplicationData(application);
          } else {
            setHasApplication(false);
            setApplicationStatus(null);
            setApplicationData(null);
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error checking user status:', error);
        setLoading(false);
      }
    };

    checkUserStatus();
  }, [user, isAdmin]);

  // Check application window status
  useEffect(() => {
    const checkApplicationWindow = async () => {
      try {
        // Get all cohorts and check their application windows
        const cohortsQuery = query(collection(db, 'cohorts'), orderBy('createdAt', 'desc'));
        
        const unsubscribe = onSnapshot(cohortsQuery, (snapshot) => {
          const now = new Date();
          let activeWindow = null;
          
          console.log('Checking application windows...', { 
            currentTime: now.toISOString(), 
            cohortsFound: snapshot.docs.length 
          });
          
          // Check each cohort for active application windows
          for (const doc of snapshot.docs) {
            const cohort = doc.data();
            const phase1Start = cohort.timeline?.phase1Start ? new Date(cohort.timeline.phase1Start) : null;
            const phase1End = cohort.timeline?.phase1Deadline ? new Date(cohort.timeline.phase1Deadline) : null;
            
            console.log('Checking cohort:', {
              name: cohort.name,
              status: cohort.status,
              phase1Start: phase1Start?.toISOString(),
              phase1End: phase1End?.toISOString(),
              isOpen: phase1Start && phase1End && now >= phase1Start && now <= phase1End
            });
            
            if (phase1Start && phase1End) {
              if (now >= phase1Start && now <= phase1End) {
                // Found an active application window
                activeWindow = {
                  status: 'open',
                  message: `Applications close on ${phase1End.toLocaleDateString()} at ${phase1End.toLocaleTimeString()}.`,
                  cohortName: cohort.name,
                  phase1Start: phase1Start,
                  phase1End: phase1End
                };
                break; // Use the first active window found
              } else if (now < phase1Start) {
                // Future window - keep as backup if no active window found
                if (!activeWindow) {
                  activeWindow = {
                    status: 'upcoming',
                    message: `Applications will open on ${phase1Start.toLocaleDateString()} at ${phase1Start.toLocaleTimeString()} and close on ${phase1End.toLocaleDateString()} at ${phase1End.toLocaleTimeString()}.`,
                    cohortName: cohort.name,
                    phase1Start: phase1Start,
                    phase1End: phase1End
                  };
                }
              }
            }
          }
          
          // Set the application window status
          if (activeWindow) {
            console.log('Setting active window:', activeWindow);
            setApplicationWindow(activeWindow);
          } else {
            console.log('No active window found');
            setApplicationWindow({
              status: 'none',
              message: 'No application window is currently scheduled.',
              cohortName: null,
              phase1Start: null,
              phase1End: null
            });
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error checking application window:', error);
        
        // Fallback: try a one-time query instead of real-time listener
        try {
          const cohortsSnapshot = await getDocs(query(collection(db, 'cohorts'), orderBy('createdAt', 'desc')));
          const now = new Date();
          let activeWindow = null;
          
          console.log('Fallback query - cohorts found:', cohortsSnapshot.docs.length);
          
          for (const doc of cohortsSnapshot.docs) {
            const cohort = doc.data();
            const phase1Start = cohort.timeline?.phase1Start ? new Date(cohort.timeline.phase1Start) : null;
            const phase1End = cohort.timeline?.phase1Deadline ? new Date(cohort.timeline.phase1Deadline) : null;
            
            console.log('Fallback checking cohort:', {
              name: cohort.name,
              phase1Start: phase1Start?.toISOString(),
              phase1End: phase1End?.toISOString(),
              isOpen: phase1Start && phase1End && now >= phase1Start && now <= phase1End
            });
            
            if (phase1Start && phase1End && now >= phase1Start && now <= phase1End) {
              activeWindow = {
                status: 'open',
                message: `Applications close on ${phase1End.toLocaleDateString()} at ${phase1End.toLocaleTimeString()}.`,
                cohortName: cohort.name,
                phase1Start: phase1Start,
                phase1End: phase1End
              };
              break;
            }
          }
          
          if (activeWindow) {
            console.log('Fallback found active window:', activeWindow);
            setApplicationWindow(activeWindow);
          } else {
            setApplicationWindow({
              status: 'none',
              message: 'No application window is currently scheduled.',
              cohortName: null,
              phase1Start: null,
              phase1End: null
            });
          }
        } catch (fallbackError) {
          console.error('Fallback query failed:', fallbackError);
          setApplicationWindow({
            status: 'error',
            message: 'Unable to check application status. Please try refreshing the page.',
            cohortName: null,
            phase1Start: null,
            phase1End: null
          });
        }
      }
    };

    checkApplicationWindow();
  }, []);

  // Load session details when application data is available
  useEffect(() => {
    const loadSessionDetails = async () => {
      if (!applicationData?.selectedSessions || !applicationData?.cohortId) return;

      try {
        const cohortDoc = await getDocs(query(collection(db, 'cohorts'), where('__name__', '==', applicationData.cohortId)));
        if (!cohortDoc.empty) {
          const cohortData = cohortDoc.docs[0].data();
          const allSessions = cohortData.phase2Sessions || [];
          
          // Filter to only the user's selected sessions
          const userSessions = allSessions.filter(session => 
            applicationData.selectedSessions.includes(session.id)
          );
          
          setSessionDetails(userSessions);
        }
      } catch (error) {
        console.error('Error loading session details:', error);
      }
    };

    loadSessionDetails();
  }, [applicationData]);

  // Temporary reset function for testing
  const resetApplication = async () => {
    if (!user || !hasApplication) return;
    
    if (!window.confirm('Are you sure you want to delete your entire application? This is for testing only.')) {
      return;
    }
    
    setResetting(true);
    try {
      // Find and delete user's application
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(applicationsQuery);
      
      for (const docRef of querySnapshot.docs) {
        await deleteDoc(docRef.ref);
      }
      
      setHasApplication(false);
      setApplicationStatus(null);
      setResetting(false);
    } catch (error) {
      console.error('Error resetting application:', error);
      alert('Failed to reset application');
      setResetting(false);
    }
  };

  // Reset only Phase 3 data for testing
  const resetPhase3Only = async () => {
    if (!user || !hasApplication) return;
    
    if (!window.confirm('Are you sure you want to reset only your Phase 3 data? This is for testing Phase 3 form.')) {
      return;
    }
    
    setResetting(true);
    try {
      // Find user's application and clear Phase 3 specific fields
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(applicationsQuery);
      
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const phase3ResetData = {
          // Clear Phase 3 specific fields
          productStage: null,
          tractionDetails: null,
          problemCustomer: null,
          videoPitchUrl: null,
          capacity: null,
          previousPartners: null,
          workedTogether: null,
          equitySplit: null,
          capTable: null,
          capTableTotals: null,
          fundingStage: null,
          fundingAmount: null,
          hasRaised: null,
          fundingDetails: null,
          fundingPlans: null,
          incorporated: null,
          incorporationLocation: null,
          ipAssignment: null,
          founderVesting: null,
          founderBoardSeats: null,
          openToAmending: null,
          amendingExplanation: null,
          incorporationAgreement: null,
          incorporationConcerns: null,
          legalAdditionalInfo: null,
          phase3RedFlags: null,
          phase3YellowFlags: null,
          phase3SubmittedAt: null,
          phase3InProgress: null,
          phase3LastSaved: null,
          // Reset phase and status to Phase 2
          currentPhase: 2,
          status: 'phase2_invited',
          updatedAt: new Date()
        };
        
        await updateDoc(docRef, phase3ResetData);
      }
      
      setResetting(false);
    } catch (error) {
      console.error('Error resetting Phase 3 data:', error);
      alert('Failed to reset Phase 3 data');
      setResetting(false);
    }
  };

  // Delete user account completely
  const deleteAccount = async () => {
    if (!user) return;
    
    setDeletingAccount(true);
    try {
      // Delete user's application documents
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      for (const docRef of applicationsSnapshot.docs) {
        await deleteDoc(docRef.ref);
      }
      
      // Delete user's Phase 3 applications
      const phase3Query = query(
        collection(db, 'phase3Applications'),
        where('userId', '==', user.uid)
      );
      const phase3Snapshot = await getDocs(phase3Query);
      
      for (const docRef of phase3Snapshot.docs) {
        await deleteDoc(docRef.ref);
      }
      
      // Delete Firebase Auth user account
      await deleteUser(user);
      
      // User will be automatically logged out and redirected
      alert('Your account has been deleted successfully. You can create a new account anytime if needed.');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again or contact support.');
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  // Submit Phase 2 verification code for a specific session
  const submitPhase2Code = async (sessionKey) => {
    const code = phase2Codes[sessionKey];
    if (!code.trim() || code.length !== 8) {
      alert('Please enter a valid 8-character code');
      return;
    }
    
    setCodeSubmitting(prev => ({ ...prev, [sessionKey]: true }));
    setCodeResults(prev => ({ ...prev, [sessionKey]: null }));
    
    try {
      // Direct database check - find the session with this code
      const cohortsQuery = query(collection(db, 'cohorts'));
      const cohortsSnapshot = await getDocs(cohortsQuery);
      
      let foundSession = null;
      let foundCohort = null;
      
      // Search all cohorts for a session with this code
      for (const cohortDoc of cohortsSnapshot.docs) {
        const cohortData = cohortDoc.data();
        const sessions = cohortData.phase2Sessions || [];
        
        const session = sessions.find(s => s.code === code.trim().toUpperCase());
        if (session) {
          foundSession = session;
          foundCohort = { id: cohortDoc.id, ...cohortData };
          break;
        }
      }
      
      if (!foundSession) {
        setCodeResults(prev => ({ 
          ...prev, 
          [sessionKey]: {
            type: 'error',
            message: 'Invalid code. Please check the code and try again.'
          }
        }));
        return;
      }
      
      // Update user's application to Phase 3
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      const appSnapshot = await getDocs(applicationsQuery);
      
      if (!appSnapshot.empty) {
        const appDoc = appSnapshot.docs[0];
        await updateDoc(appDoc.ref, {
          currentPhase: 3,
          status: 'phase3_invited',
          phase2CompletedAt: new Date(),
          verifiedSessions: [foundSession.id],
          [`phase2SessionInfo_${foundSession.id}`]: {
            cohortId: foundCohort.id,
            sessionId: foundSession.id,
            sessionDate: foundSession.date,
            sessionTime: foundSession.time,
            verifiedAt: new Date()
          },
          updatedAt: new Date()
        });
        
        setCodeResults(prev => ({ 
          ...prev, 
          [sessionKey]: {
            type: 'success',
            message: `Code verified! You've been advanced to Phase 3. Session: ${foundSession.date} at ${foundSession.time}`,
            sessionInfo: foundSession
          }
        }));
        
        setPhase2Codes(prev => ({ ...prev, [sessionKey]: '' }));
      }
      
    } catch (error) {
      console.error('Error verifying code:', error);
      setCodeResults(prev => ({ 
        ...prev, 
        [sessionKey]: {
          type: 'error',
          message: 'Failed to verify code. Please try again.'
        }
      }));
    } finally {
      setCodeSubmitting(prev => ({ ...prev, [sessionKey]: false }));
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'auto_approved': 'bg-green-100 text-green-800',
      'approved_phase2': 'bg-green-100 text-green-800',
      'auto_rejected': 'bg-red-100 text-red-800',
      'rejected_manual': 'bg-red-100 text-red-800',
      'manual_review': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get current phase info
  const getPhaseInfo = () => {
    const phases = [
      {
        number: 1,
        title: "Initial Application",
        description: "Submit your application and company information. We'll review your background and business concept.",
        icon: "fas fa-file-alt"
      },
      {
        number: 2, 
        title: "Group Session",
        description: "Attend virtual group sessions to learn about the program and network with other candidates.",
        icon: "fas fa-users"
      },
      {
        number: 3,
        title: "In-Depth Application", 
        description: "Complete detailed application with business plan, financials, and team information.",
        icon: "fas fa-clipboard-list"
      },
      {
        number: 4,
        title: "Interview Process",
        description: "One-on-one interviews with program leaders to finalize your acceptance.",
        icon: "fas fa-handshake"
      }
    ];

    const currentPhase = applicationData?.currentPhase || 1;
    return { phases, currentPhase };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to VB Accelerator
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Empowering combat veterans to build venture-scale companies
          </p>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed">
              The Version Bravo Accelerator Program is designed for ambitious founders from the 
              combat veteran community. Our mission is to provide the tools, network, and resources 
              necessary to scale your business to the next level.
            </p>
          </div>
        </div>

        {/* Program Overview */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-users text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Network</h3>
              <p className="text-gray-600">
                Connect with successful entrepreneurs, investors, and industry leaders who understand your journey.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-rocket text-green-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Growth Acceleration</h3>
              <p className="text-gray-600">
                Structured program to help you scale from early-stage startup to venture-ready company.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-handshake text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Veteran Community</h3>
              <p className="text-gray-600">
                Join a community of like-minded veterans who share your drive and understand your background.
              </p>
            </div>
          </div>
        </div>

        {/* Application Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {hasApplication ? (
            // User has already submitted an application - Show Visual Progress
            <div>
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Your VB Accelerator Journey</h2>
                <p className="text-gray-600">Track your progress through our 4-phase selection process</p>
              </div>

              {/* Visual Phase Progress */}
              {(() => {
                const { phases, currentPhase } = getPhaseInfo();
                return (
                  <div className="mb-8">
                    {/* Progress Bar */}
                    <div className="flex items-center justify-between mb-8">
                      {phases.map((phase, index) => (
                        <div key={phase.number} className="flex items-center">
                          {/* Phase Circle */}
                          <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                            phase.number <= currentPhase 
                              ? 'bg-blue-600 border-blue-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-400'
                          }`}>
                            {phase.number < currentPhase ? (
                              <i className="fas fa-check text-sm"></i>
                            ) : phase.number === currentPhase ? (
                              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                            ) : (
                              <span className="text-sm font-bold">{phase.number}</span>
                            )}
                          </div>
                          
                          {/* Connection Line */}
                          {index < phases.length - 1 && (
                            <div className={`flex-1 h-1 mx-4 transition-all ${
                              phase.number < currentPhase ? 'bg-blue-600' : 'bg-gray-200'
                            }`}></div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Phase Details */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                      {phases.map((phase) => (
                        <div key={phase.number} className={`p-6 rounded-lg border-2 transition-all ${
                          phase.number === currentPhase 
                            ? 'border-blue-500 bg-blue-50 shadow-lg' 
                            : phase.number < currentPhase
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="text-center">
                            <div className={`w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center ${
                              phase.number === currentPhase 
                                ? 'bg-blue-600 text-white' 
                                : phase.number < currentPhase
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-400 text-white'
                            }`}>
                              <i className={`${phase.icon} text-lg`}></i>
                            </div>
                            <h3 className={`font-semibold mb-2 ${
                              phase.number === currentPhase ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              Phase {phase.number}: {phase.title}
                            </h3>
                            <p className="text-sm text-gray-600">{phase.description}</p>
                            
                            {/* Phase Status Badge */}
                            <div className="mt-3">
                              {phase.number < currentPhase && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <i className="fas fa-check mr-1"></i> Completed
                                </span>
                              )}
                              {phase.number === currentPhase && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <i className="fas fa-circle mr-1 animate-pulse"></i> Current Phase
                                </span>
                              )}
                              {phase.number > currentPhase && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  <i className="fas fa-clock mr-1"></i> Upcoming
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Current Status and Actions */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(applicationStatus)}`}>
                    {applicationStatus?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>

                {/* Phase-specific content */}
                {applicationData?.currentPhase === 1 && (
                  <div>
                    {applicationStatus === 'pending' && (
                      <p className="text-gray-600">Your application is in the queue for initial review. We'll contact you within 5-7 business days.</p>
                    )}
                    {applicationStatus === 'manual_review' && (
                      <p className="text-gray-600">Your application is under manual review by our team. We'll contact you with next steps soon.</p>
                    )}
                  </div>
                )}

                {applicationData?.currentPhase === 2 && (
                  <div>
                    <div className="flex items-center mb-4">
                      <i className="fas fa-trophy text-green-600 mr-2"></i>
                      <p className="text-green-600 font-medium">Congratulations! You've been approved for Phase 2 Group Sessions.</p>
                    </div>
                    
                    {/* Session Code Inputs */}
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <p className="text-green-800 text-sm">
                          <i className="fas fa-info-circle mr-2"></i>
                          <strong>You only need to attend ONE session.</strong> Attend whichever session works best for your schedule and enter the verification code you receive.
                        </p>
                      </div>
                      
                      {/* Dynamic Session Inputs based on user's selected sessions */}
                      {sessionDetails.length > 0 && (
                        <>
                          {sessionDetails.map((session, index) => {
                            const sessionKey = `session${index + 1}`;
                            const colors = ['blue', 'purple', 'green', 'orange'];
                            const color = colors[index % colors.length];
                            
                            const sessionDate = new Date(session.date);
                            const formattedDate = sessionDate.toLocaleDateString('en-US', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            });
                            
                            return (
                              <div key={sessionKey} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
                                <div className="flex justify-between items-start mb-3">
                                  <h4 className={`font-medium text-${color}-900 flex items-center`}>
                                    <i className="fas fa-calendar mr-2"></i>
                                    {formattedDate} {session.time} Session - Verification Code
                                  </h4>
                                  {session.zoomLink && (
                                    <a 
                                      href={session.zoomLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`text-${color}-700 hover:text-${color}-900 text-sm flex items-center ml-4`}
                                    >
                                      <i className="fas fa-video mr-1"></i>
                                      Join Zoom
                                    </a>
                                  )}
                                </div>
                                <div className="flex space-x-3">
                                  <input
                                    type="text"
                                    value={phase2Codes[sessionKey] || ''}
                                    onChange={(e) => setPhase2Codes(prev => ({ ...prev, [sessionKey]: e.target.value.toUpperCase() }))}
                                    placeholder="Enter 8-character code"
                                    maxLength="8"
                                    className={`flex-1 px-3 py-2 border border-${color}-300 rounded-md focus:outline-none focus:ring-2 focus:ring-${color}-500 font-mono text-lg tracking-wider`}
                                  />
                                  <button
                                    onClick={() => submitPhase2Code(sessionKey)}
                                    disabled={codeSubmitting[sessionKey] || (phase2Codes[sessionKey] || '').length !== 8}
                                    className={`bg-${color}-600 text-white px-6 py-2 rounded-md hover:bg-${color}-700 disabled:opacity-50`}
                                  >
                                    {codeSubmitting[sessionKey] ? 'Verifying...' : 'Verify'}
                                  </button>
                                </div>
                                
                                {codeResults[sessionKey] && (
                                  <div className={`mt-3 p-3 rounded-md ${
                                    codeResults[sessionKey].type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                                  }`}>
                                    <p className="font-medium">{codeResults[sessionKey].message}</p>
                                    {codeResults[sessionKey].sessionInfo && (
                                      <p className="text-sm mt-1">
                                        Session: {new Date(codeResults[sessionKey].sessionInfo.sessionDate).toLocaleDateString()} at {codeResults[sessionKey].sessionInfo.sessionTime}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Fallback if session details haven't loaded yet */}
                      {applicationData?.selectedSessions && sessionDetails.length === 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="text-gray-600 text-center">
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Loading your session details...
                          </p>
                        </div>
                      )}

                      <p className="text-xs text-gray-600 mt-2">
                        <i className="fas fa-info-circle mr-1"></i>
                        You'll receive a verification code during the Group Session you attend. Only one code verification is required to advance to Phase 3.
                      </p>
                    </div>
                  </div>
                )}

                {applicationData?.currentPhase === 3 && (
                  <div>
                    <div className="flex items-center mb-4">
                      <i className="fas fa-check-circle text-blue-600 mr-2"></i>
                      <p className="text-blue-600 font-medium">Phase 2 completed! You're now in Phase 3.</p>
                    </div>
                    <p className="text-gray-600 mb-4">
                      Complete your In-Depth Application to continue your VB Accelerator journey. This detailed form covers your business plan, team structure, funding history, and legal setup.
                    </p>
                    
                    {/* Check Phase 3 application status */}
                    {applicationData?.phase3SubmittedAt ? (
                      <div className={`p-4 rounded-lg mb-4 ${
                        applicationData.status === 'phase3_approved' ? 'bg-green-50 border border-green-200' :
                        applicationData.status === 'phase3_pending' || applicationData.status === 'phase3_failed' ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          <i className={`fas mr-2 ${
                            applicationData.status === 'phase3_approved' ? 'fa-check-circle text-green-600' :
                            applicationData.status === 'phase3_pending' ? 'fa-clock text-yellow-600' :
                            applicationData.status === 'phase3_failed' ? 'fa-exclamation-triangle text-red-600' :
                            'fa-clock text-yellow-600'
                          }`}></i>
                          <span className="font-medium">
                            Phase 3 Application: {
                              applicationData.status === 'phase3_approved' ? 'Approved' :
                              applicationData.status === 'phase3_pending' ? 'Under Review' :
                              applicationData.status === 'phase3_failed' ? 'Needs Revision' :
                              'Submitted'
                            }
                          </span>
                        </div>
                        {applicationData.status === 'phase3_approved' && (
                          <p className="text-green-700 mt-2">Your Phase 3 application has been approved! You'll advance to Phase 4 interviews soon.</p>
                        )}
                        {applicationData.status === 'phase3_pending' && (
                          <p className="text-yellow-700 mt-2">Your Phase 3 application is under review by our team.</p>
                        )}
                        {applicationData.status === 'phase3_failed' && (
                          <p className="text-red-700 mt-2">Your Phase 3 application needs revision. Please review and resubmit.</p>
                        )}
                        <p className="text-gray-600 text-sm mt-2">
                          Submitted: {new Date(applicationData.phase3SubmittedAt.seconds * 1000).toLocaleDateString()}
                        </p>
                      </div>
                    ) : applicationData?.phase3InProgress ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-yellow-900 mb-1">Phase 3 Draft Saved</h4>
                            <p className="text-yellow-700 text-sm">Continue working on your in-depth application. Your progress has been saved.</p>
                            <p className="text-yellow-600 text-xs mt-1">
                              Last saved: {new Date(applicationData.phase3LastSaved?.seconds * 1000 || Date.now()).toLocaleString()}
                            </p>
                          </div>
                          <button
                            onClick={() => window.location.href = '/phase3'}
                            className="bg-yellow-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors"
                          >
                            Continue Application
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-blue-900 mb-1">Ready to Submit Phase 3?</h4>
                            <p className="text-blue-700 text-sm">Complete your in-depth application with business details, cap table, and legal structure.</p>
                          </div>
                          <button
                            onClick={() => window.location.href = '/phase3'}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                          >
                            Start Phase 3 Application
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {applicationData?.currentPhase >= 4 && (
                  <div>
                    <div className="flex items-center mb-4">
                      <i className="fas fa-bullseye text-purple-600 mr-2"></i>
                      <p className="text-purple-600 font-medium">Congratulations! You're in Phase 4 - Interview Process!</p>
                    </div>
                    {applicationData?.assignedInterviewer ? (
                      <div className="space-y-3">
                        <p className="text-gray-600">
                          Please schedule your interview if you haven't already with <strong>{applicationData.assignedInterviewer.name}</strong>
                        </p>
                        {applicationData.assignedInterviewer.calendarLink && (
                          <div className="mb-3">
                            <a 
                              href={applicationData.assignedInterviewer.calendarLink}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 transition-colors"
                            >
                              <i className="fas fa-calendar-alt mr-2"></i>
                              Schedule Interview
                            </a>
                          </div>
                        )}
                        <p className="text-gray-600">
                          For any inquiries, reach out to your interviewer: <a href={`mailto:${applicationData.assignedInterviewer.email}`} className="text-purple-600 hover:text-purple-800">{applicationData.assignedInterviewer.email}</a>
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-600">We'll contact you to schedule your one-on-one interview with our program leaders.</p>
                    )}
                  </div>
                )}

                {(applicationStatus === 'auto_rejected' || applicationStatus === 'rejected_manual') && (
                  <div>
                    <div className="flex items-center mb-4">
                      <i className="fas fa-times-circle text-red-600 mr-2"></i>
                      <p className="text-red-600 font-medium">Application Status Update</p>
                    </div>
                    <p className="text-gray-600 mb-2">Unfortunately, your application was not selected for this cohort.</p>
                    <p className="text-gray-600">Thank you for your interest. Please feel free to apply for future programs.</p>
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div className="text-center text-gray-500">
                <p>Questions? Contact us at <a href="mailto:eden@versionbravo.com" className="text-blue-600 hover:text-blue-800">eden@versionbravo.com</a></p>
              </div>

              {/* Delete Account Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Delete Account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone, but you can create a new account later if needed.
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Delete My Account
                  </button>
                </div>
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <div className="flex items-center mb-4">
                      <i className="fas fa-exclamation-triangle text-red-600 text-xl mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">Confirm Account Deletion</h3>
                    </div>
                    <p className="text-gray-600 mb-6">
                      Are you sure you want to permanently delete your account? This will remove:
                    </p>
                    <ul className="text-sm text-gray-600 mb-6 space-y-1">
                      <li>• Your user account and login credentials</li>
                      <li>• All application data and submissions</li>
                      <li>• Phase progress and notes</li>
                      <li>• All associated files and documents</li>
                    </ul>
                    <p className="text-sm text-red-600 mb-6 font-medium">
                      This action cannot be undone, but you can create a new account later if needed.
                    </p>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deletingAccount}
                        className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={deleteAccount}
                        disabled={deletingAccount}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {deletingAccount ? 'Deleting...' : 'Delete Account'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Temporary Testing Buttons */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Testing Mode:</strong> These buttons allow you to reset applications for testing purposes.
                  </p>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={resetApplication}
                    disabled={resetting}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {resetting ? 'Deleting...' : 'Submit Another Full Application'}
                  </button>
                  <button
                    onClick={resetPhase3Only}
                    disabled={resetting}
                    className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50"
                  >
                    {resetting ? 'Resetting...' : 'Submit Another Phase 3 Application'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // User hasn't applied yet - show application status based on window
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">VB Accelerator Application</h2>
                {applicationWindow && (
                  <div className={`p-4 rounded-lg mb-6 ${
                    applicationWindow.status === 'open' ? 'bg-green-50 border border-green-200' :
                    applicationWindow.status === 'upcoming' ? 'bg-blue-50 border border-blue-200' :
                    'bg-gray-50 border border-gray-200'
                  }`}>
                    {applicationWindow.status === 'open' && (
                      <div>
                        <h3 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
                          <i className="fas fa-bullseye mr-2"></i>
                          Applications Open for {applicationWindow.cohortName}
                        </h3>
                        <p className="text-green-700">{applicationWindow.message}</p>
                      </div>
                    )}
                    
                    {applicationWindow.status === 'upcoming' && (
                      <div>
                        <h3 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                          <i className="fas fa-calendar-alt mr-2"></i>
                          Upcoming Application Window
                        </h3>
                        <p className="text-blue-700 mb-2">
                          <strong>{applicationWindow.cohortName}</strong>
                        </p>
                        <p className="text-blue-700">{applicationWindow.message}</p>
                      </div>
                    )}
                    
                    {(applicationWindow.status === 'closed' || applicationWindow.status === 'none') && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          <i className="fas fa-clock mr-2"></i>
                          Applications Currently Closed
                        </h3>
                        <p className="text-gray-700">{applicationWindow.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Only show application process if window is open */}
              {applicationWindow?.status === 'open' && (
                <>
                  <div className="mb-8">
                    <p className="text-gray-600 mb-6 text-center">
                      Take the first step toward joining our {applicationWindow.cohortName} cohort. The application process consists of 
                      multiple phases designed to ensure the best fit for both you and the program.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-4 gap-6 mb-8">
                    <div className="text-center">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-sm font-bold">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phase 1</h4>
                      <p className="text-sm text-gray-600">Initial Application</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-sm font-bold">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-700 mb-1">Phase 2</h4>
                      <p className="text-sm text-gray-600">Group Session</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-sm font-bold">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-700 mb-1">Phase 3</h4>
                      <p className="text-sm text-gray-600">In-Depth Application</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-sm font-bold">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-700 mb-1">Phase 4</h4>
                      <p className="text-sm text-gray-600">Interview</p>
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={() => window.location.href = '/application'}
                      className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Start Phase 1 Application
                    </button>
                    <p className="text-sm text-gray-500 mt-3">
                      Takes approximately 5-10 minutes to complete
                    </p>
                  </div>
                </>
              )}

            </div>
          )}
        </div>

        {/* Program Details */}
        <div className="mt-12 bg-gray-100 rounded-lg p-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Program Requirements</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Eligibility Criteria</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="fas fa-check text-green-600 mt-1 mr-3"></i>
                    <span>Combat veteran or reservist (US or Israel)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check text-green-600 mt-1 mr-3"></i>
                    <span>Founder or co-founder of a venture-scale company</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check text-green-600 mt-1 mr-3"></i>
                    <span>Committed to full program participation</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check text-green-600 mt-1 mr-3"></i>
                    <span>Professional communication and presentation skills</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Commitment</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="fas fa-clock text-blue-600 mt-1 mr-3"></i>
                    <span>Weekly virtual sessions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-clock text-blue-600 mt-1 mr-3"></i>
                    <span>Multi-day intensive workshops</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-clock text-blue-600 mt-1 mr-3"></i>
                    <span>Travel required for in-person modules</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-clock text-blue-600 mt-1 mr-3"></i>
                    <span>100% attendance mandatory</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;